import React, { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import PublicLayout from "../components/PublicLayout";
import api from "../lib/api";

// Parse DD/MM/YYYY → Date object (local)
function parseDDMMYYYY(s) {
  if (!s) return null;
  const parts = String(s).split("/");
  if (parts.length !== 3) return null;
  const [d, m, y] = parts.map((p) => parseInt(p, 10));
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function TeerCalendar() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Date());

  useEffect(() => {
    api
      .get("/previous", { params: { published_only: true } })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const byDate = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const d = parseDDMMYYYY(r.date);
      if (d) map.set(d.toDateString(), r);
    }
    return map;
  }, [rows]);

  const markedDays = useMemo(
    () => Array.from(byDate.values()).map((r) => parseDDMMYYYY(r.date)).filter(Boolean),
    [byDate]
  );

  const selectedResult = selected ? byDate.get(selected.toDateString()) : null;

  return (
    <PublicLayout title="Teer Calendar">
      <div className="nt-container mt-6">
        <h2 className="font-display text-2xl text-center mb-3">Teer Calendar</h2>
        <p className="text-center text-sm text-slate-600 mb-4">
          Click any highlighted date to view that day's result.
        </p>

        <div className="bg-white rounded-xl border p-3 sm:p-4 shadow-sm flex justify-center">
          <DayPicker
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            modifiers={{ hasResult: markedDays }}
            modifiersClassNames={{
              hasResult:
                "!bg-emerald-100 !text-emerald-800 !rounded-full !font-extrabold",
              selected: "!bg-emerald-600 !text-white !rounded-full",
            }}
            showOutsideDays
            data-testid="teer-calendar"
          />
        </div>

        <div
          className="mt-4 bg-white rounded-xl border p-4 shadow-sm text-center"
          data-testid="calendar-result"
        >
          {loading ? (
            <div className="text-slate-500">Loading…</div>
          ) : selectedResult ? (
            <>
              <div className="font-display text-lg mb-1">
                {selectedResult.city} — {selectedResult.date}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="border rounded-lg py-3">
                  <div className="text-xs text-slate-500">F/R</div>
                  <div className="nt-num" data-testid="cal-fr">
                    {selectedResult.fr || "-"}
                  </div>
                </div>
                <div className="border rounded-lg py-3">
                  <div className="text-xs text-slate-500">S/R</div>
                  <div className="nt-num" data-testid="cal-sr">
                    {selectedResult.sr || "-"}
                  </div>
                </div>
              </div>
              {selectedResult.text ? (
                <p className="text-sm text-slate-700 mt-2">{selectedResult.text}</p>
              ) : null}
            </>
          ) : (
            <div className="text-slate-500">
              {`No result published for ${
                selected ? selected.toLocaleDateString("en-GB") : "the selected date"
              }.`}
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
